<template>
  <div class="flex h-screen w-full">
    <!-- 主内容区 -->
    <main class="flex-1 flex flex-col p-6 lg:p-8">
      <div class="flex h-full w-full flex-col justify-end">
        <div class="flex flex-col gap-4">
          <h3 class="text-white text-lg font-bold leading-tight tracking-[-0.015em] px-4 pb-2 pt-4">AI Script Generator</h3>

          <!-- 生成结果显示区域 -->
          <div class="p-4 @container" v-if="generatedScript">
            <div class="flex flex-col items-stretch justify-start rounded-lg bg-[#1A1A1D] p-4">
              <div class="flex min-w-72 grow flex-col items-stretch justify-center gap-2">
                <div class="flex items-center justify-between">
                  <p class="text-lg font-bold leading-tight tracking-[-0.015em] text-white">Generated Script</p>
                  <div class="flex gap-2">
                    <button
                      @click="copyScript"
                      class="flex h-8 cursor-pointer items-center justify-center overflow-hidden rounded-md bg-[#2C2F33] px-3 text-white text-sm font-medium leading-normal hover:bg-[#3a3e42]"
                    >
                      <span class="material-symbols-outlined text-base mr-1">content_copy</span>
                      <span class="truncate">Copy</span>
                    </button>
                    <button
                      @click="goToDashboard"
                      class="flex h-8 cursor-pointer items-center justify-center overflow-hidden rounded-md bg-primary px-3 text-white text-sm font-medium leading-normal hover:bg-primary/90"
                    >
                      <span class="material-symbols-outlined text-base mr-1">arrow_back</span>
                      <span class="truncate">Back to Dashboard</span>
                    </button>
                  </div>
                </div>
                <p class="text-slate-300 text-base font-normal leading-normal whitespace-pre-line">{{ generatedScript }}</p>
              </div>
            </div>
          </div>

          <!-- 输入区域 -->
          <div class="flex max-w-full flex-col items-end gap-4 px-4 py-3">
            <div class="relative w-full">
              <textarea
                v-model="scriptPrompt"
                class="form-input flex w-full min-w-0 flex-1 resize-none overflow-auto rounded-lg border-none bg-[#1A1A1D] p-4 pr-12 text-base font-normal leading-normal text-white placeholder:text-slate-400 focus:outline-0 focus:ring-2 focus:ring-primary min-h-36"
                :placeholder="scriptPlaceholder"
                maxlength="500"
                @keydown="handleKeyDown"
              ></textarea>
              <button
                @click="startVoiceRecording"
                :class="isRecording ? 'bg-red-500' : 'bg-[#2C2F33] hover:bg-[#3a3e42]'"
                class="absolute bottom-3 right-3 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-white"
              >
                <span v-if="!isRecording" class="material-symbols-outlined text-xl">mic</span>
                <span v-else class="flex h-4 w-4">
                  <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span class="relative inline-flex rounded-full h-4 w-4 bg-red-500"></span>
                </span>
              </button>
            </div>
            <div class="flex w-full items-center justify-between">
              <button
                @click="goToDashboard"
                class="flex items-center gap-2 px-4 py-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
              >
                <span class="material-symbols-outlined text-base">arrow_back</span>
                <span class="text-sm font-medium">Cancel</span>
              </button>
              <div class="flex items-center gap-3">
                <p class="text-slate-400 text-sm font-normal leading-normal">{{ scriptPrompt.length }}/500</p>
                <button
                  @click="generateScript"
                  :disabled="!canGenerate"
                  class="flex min-w-[140px] max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-10 px-4 text-black text-sm font-bold leading-normal tracking-[0.015em]"
                  :class="canGenerate ? 'bg-primary hover:opacity-90' : 'bg-slate-600 cursor-not-allowed'"
                >
                  <span class="truncate">{{ isGenerating ? 'Generating...' : 'Generate Script' }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { getApiUrl } from '@/config/apiConfig';

export default {
  name: 'ScriptGenerator',
  data() {
    return {
      scriptPrompt: '',
      generatedScript: '',
      isRecording: false,
      recognition: null,
      isGenerating: false,
      streamerName: '',
      streamerAvatar: '/avatar_default.png',
      // 话术建议相关数据
      showSuggestionPanel: false,
      suggestionData: {
        name: '',
        avatar: '',
        message: '',
        interactionProbability: '',
        previousAppearances: '',
        tag: '',
        sameLanguageResponse: '',
        differentLanguageResponse: '',
        phoneticGuide: '',
        translation: '',
        priorityRating: 0
      },
      languageMode: 'same',
      selectedSourceLanguage: 'ja',
      selectedTargetLanguage: 'zh',
      originalTranslation: '',
      suggestionTranslation: '',
      isTranslating: false,
      fontSize: 16,
      minFontSize: 12,
      maxFontSize: 24
    }
  },
  computed: {
    canGenerate() {
      return this.scriptPrompt.trim().length > 0 && !this.isGenerating && !this.isRecording;
    },
    scriptPlaceholder() {
      return "Enter your instructions for the AI... e.g., 'Thank a new follower named @user123', 'How to upsell a gift?', 'Encourage more comments about product X.'";
    }
  },
  methods: {
    goToDashboard() {
      this.$router.push('/');
    },
    goToAnalytics() {
      this.$router.push('/analytics');
    },
    goToSettings() {
      this.$router.push('/settings');
    },
    goToOnboarding() {
      this.$router.push('/onboarding');
    },

    async generateScript() {
      if (!this.canGenerate) return;

      this.isGenerating = true;
      this.generatedScript = '';

      try {
        // 先将用户输入和用户信息保存到localStorage，供Dashboard.vue使用
        const userInfo = {
          prompt: this.scriptPrompt,
          userName: '脚本生成', // 可以根据需要调整
          userAvatar: '/avatar_default.png' // 使用默认头像
        };
        localStorage.setItem('scriptGenerationPending', JSON.stringify(userInfo));

        // 跳转回Dashboard（让Dashboard处理API调用和显示）
        this.$router.push('/');
      } catch (error) {
        console.error('准备生成脚本时出错:', error);
        this.generatedScript = '准备生成脚本时发生错误，请重试。';
        this.isGenerating = false;
      }
    },

    async callScriptGenerationAPI(prompt) {
      try {
        // 获取token
        const token = localStorage.getItem('katoken');
        if (!token) {
          throw new Error('用户未登录');
        }

        // 获取直播间ID和会话ID
        const bindingInfo = localStorage.getItem('tiktok-binding-info');
        const bindingData = bindingInfo ? JSON.parse(bindingInfo) : null;
        const roomId = localStorage.getItem('tiktok-live-room-id') || (bindingData && bindingData.roomId) || (bindingData && bindingData.bindingSource);
        const sessionId = localStorage.getItem('currentSessionId');

        if (!roomId || !sessionId) {
          throw new Error('未找到直播间信息，请先启动直播监控');
        }

        // 准备请求头
        const headers = {
          'req_app_id': 'LivePlugin',
          'req_app_secret': 'ewMpjg7t4vX5bK13oRZzSB8mEuNGr2Yy',
          'katoken': token,
          'Content-Type': 'application/json'
        };

        // 准备请求体 - 使用正确参数格式
        const requestBody = {
          liveRoomId: roomId,
          sessionId: parseInt(sessionId),
          description: prompt,
          llmModelName: "gemini-2.5-flash"
        };

        console.log('调用脚本生成接口:', getApiUrl('SCRIPT_GENERATION_GENERATE_BY_DESCRIPTION'));
        console.log('请求体:', requestBody);

        const response = await fetch(getApiUrl('SCRIPT_GENERATION_GENERATE_BY_DESCRIPTION'), {
          method: 'POST',
          headers: headers,
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          throw new Error(`接口调用失败: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        console.log('脚本生成接口返回:', result);
        return result;

      } catch (error) {
        console.error('调用脚本生成接口失败:', error);
        throw error;
      }
    },

    generateScriptContent(prompt) {
      const promptLower = prompt.toLowerCase();

      // 基于输入内容生成不同的脚本
      if (promptLower.includes('thank') && promptLower.includes('follower')) {
        return `Hey everyone! Big shoutout to ${this.extractUsername(prompt) || '@user123'} for joining our amazing community! 🚀\n\nWelcome aboard! We're so excited to have you here participating in the live stream. Make sure to drop your thoughts in the comments and let us know what you'd like to see next!\n\n#NewFollower #Community #Welcome`;
      }

      if (promptLower.includes('upsell') && promptLower.includes('gift')) {
        return `Hey everybody! 🎁✨ I just want to remind you all about our amazing gift options! From roses to TikTok Universe, every gift helps support the stream and gets you featured!\n\nIf you haven't sent a gift yet, now's the perfect time! Your support means the world and helps us create even better content for you.\n\nWho's ready to level up their support? 💎`;
      }

      if (promptLower.includes('encourage') && promptLower.includes('comment')) {
        return `📝💬 Let's get those comments flowing! I love hearing from you all - your thoughts, questions, and feedback make this stream so much better!\n\nDon't be shy - whether it's about the game, suggestions for future streams, or just saying hi, every comment lights up my day!\n\nWhat's on your mind? Let's chat! 🔥`;
      }

      // 默认响应
      return `"Thanks for that amazing ${prompt}! You're making this stream incredible! 🎉\n\nKeep supporting and let's make this the best stream ever! Your engagement means everything to me. 💪✨\n\n#Streaming #Support #Community`;
    },

    extractUsername(prompt) {
      const match = prompt.match(/@(\w+)/);
      return match ? `@${match[1]}` : null;
    },

    copyScript() {
      if (!this.generatedScript) return;

      navigator.clipboard.writeText(this.generatedScript).then(() => {
        // 简单的成功反馈
        alert('Script copied to clipboard!');
      }).catch(() => {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = this.generatedScript;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand('copy');
          alert('Script copied to clipboard!');
        } catch (err) {
          alert('Failed to copy script');
        }
        document.body.removeChild(textArea);
      });
    },

    startVoiceRecording() {
      if (this.isRecording) {
        this.stopVoiceRecording();
        return;
      }

      // 检查浏览器支持
      if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
        alert('Voice input is not supported in your browser. Please try Chrome, Edge, or Safari.');
        return;
      }

      this.isRecording = true;

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      this.recognition = new SpeechRecognition();

      this.recognition.continuous = false;
      this.recognition.interimResults = false;
      this.recognition.lang = 'en-US';

      this.recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        this.scriptPrompt += (this.scriptPrompt ? ' ' : '') + transcript;
      };

      this.recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        alert('Voice input failed. Please try again.');
        this.isRecording = false;
      };

      this.recognition.onend = () => {
        this.isRecording = false;
      };

      try {
        this.recognition.start();
      } catch (error) {
        console.error('Failed to start speech recognition:', error);
        this.isRecording = false;
      }
    },

    stopVoiceRecording() {
      if (this.recognition) {
        this.recognition.stop();
      }
      this.isRecording = false;
    },

    handleKeyDown(event) {
      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        this.generateScript();
      }
    }
  },
  beforeUnmount() {
    if (this.isRecording && this.recognition) {
      this.recognition.stop();
    }
  }
}
</script>

<style scoped>
.material-symbols-outlined {
  font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
}
</style>
